import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { createPortal } from "react-dom";
import { clsxMerge } from "shared/lib/helpers";

import Router from "@/helpers/router";
import { LOGO_VARIATION_MAP } from "@/components/navigation/logo";

/**
 * Logo + leave-application confirmation modal.
 *
 * Drop-in replacement for the plain Logo on flow pages (qualify,
 * orientation, form). Clicking the wordmark opens a modal instead of
 * immediately navigating. The modal warns the applicant that their
 * progress will be lost, and offers Stay/Leave.
 *
 * The modal renders via React Portal to document.body so it escapes
 * any parent transform / overflow / stacking context the page might
 * have. ESC and backdrop-click both close the modal (treated as
 * "Stay"); only the explicit "Leave anyway" button navigates away.
 *
 * If applicants ever DO leave and want to come back, sessionStorage
 * is intentionally NOT cleared on confirm — that way they can resume
 * pre-app values when they re-enter /apply/qualify or /apply/form.
 */
export default function ExitGuardLogo({
  width = "112px",
  className,
}: {
  width?: string;
  className?: string;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // ESC + body scroll lock while modal is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const handleLeave = () => {
    setOpen(false);
    router.push(Router.individual());
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Exit application and return to Glencoco home"
        className={clsxMerge(
          "inline-flex items-center",
          "rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          className
        )}
      >
        <img
          src={LOGO_VARIATION_MAP.default}
          alt="Glencoco"
          width={width}
          draggable={false}
        />
      </button>

      {mounted &&
        open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-guard-title"
          >
            <div
              className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                id="exit-guard-title"
                className="text-xl font-bold leading-tight md:text-2xl"
              >
                Leave your application?
              </h2>
              <p className="mt-3 leading-relaxed text-base-content/70">
                Your application progress will be lost. Are you sure you want to
                leave?
              </p>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-base-200 px-6 py-3 text-sm font-semibold transition-colors hover:bg-base-200/40"
                >
                  Stay here
                </button>
                <button
                  type="button"
                  onClick={handleLeave}
                  className="rounded-full bg-base-content px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-base-content/90"
                >
                  Leave anyway
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
